import React from "react";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Section from "../../../layout/Section";
import ExpandableDetails from "../../../layout/ExpandableDetails";
import Dropdown from "../../../common/inputs/Dropdown";
import StatChip from "../../../common/display/StatChip";
import { colors } from "../../../../styles/colors";
import {
  getDomainStatusColor,
  getDomainStatusIcon,
  getGesamtstatusColor,
  isPlaceholderStatus,
} from "../../../../utils/colors/gesamtstatusColor";
import { getGesamtstatusLabel } from "../../../../utils/gesamtstatusLabel";
import { GesamtstatusDomain } from "../../../../types";

interface ProjectStatusSectionProps {
  gesamtstatus: string;
  gesamtstatusDomain: GesamtstatusDomain;
  /** Letzte Stufe erreicht – vom Server, färbt den Badge grün */
  gesamtstatusAbgeschlossen: boolean;
  flaechenakquiseStatus: string;
  planungsrechtStatus: string;
  bimschgStatus: string;
  eegAusschreibungStatus: string;
  finanzierungStatus: string;
  bauStatus: string;
  flaechenakquiseOptions: string[];
  planungsrechtOptions: string[];
  bimschgOptions: string[];
  eegAusschreibungOptions: string[];
  finanzierungOptions: string[];
  bauOptions: string[];
  canEdit: boolean;
  onFlaechenakquiseChange: (value: string) => void;
  onPlanungsrechtChange: (value: string) => void;
  onBimschgChange: (value: string) => void;
  onEegAusschreibungChange: (value: string) => void;
  onFinanzierungChange: (value: string) => void;
  onBauChange: (value: string) => void;
}

const LABEL_WIDTH = 240;
const INPUT_MAX_WIDTH = 280;

/** Label mit Häkchen, sobald diese Domäne ihre letzte (abgeschlossene) Stufe erreicht hat. */
const DomainLabel: React.FC<{
  domain: GesamtstatusDomain;
  value: string;
  options: string[];
  label: string;
}> = ({ domain, value, options, label }) => {
  const icon = getDomainStatusIcon(domain, value, options);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: LABEL_WIDTH,
        minWidth: LABEL_WIDTH,
      }}
    >
      <Box
        sx={{
          width: 18,
          minWidth: 18,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {icon === "check" && (
          <CheckCircleIcon
            sx={{ fontSize: 18, color: colors.status.success.main }}
          />
        )}
      </Box>
      <Typography
        sx={{
          whiteSpace: "nowrap",
          color: colors.text.primary,
          fontWeight: 400,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

/**
 * Projektstatus-Section des Projektrahmens.
 * Zeigt den abgeleiteten Gesamtstatus (nicht editierbar) sowie die 6 unabhängigen
 * Domänen-Status untereinander, jeweils editierbar über ein Dropdown (Optionen kommen
 * vom jeweiligen Backend-Options-Endpoint). Ein Haken vor dem Domänen-Label zeigt, sobald
 * diese Domäne ihre höchste Stufe erreicht hat.
 */
const ProjectStatusSection: React.FC<ProjectStatusSectionProps> = ({
  gesamtstatus,
  gesamtstatusDomain,
  gesamtstatusAbgeschlossen,
  flaechenakquiseStatus,
  planungsrechtStatus,
  bimschgStatus,
  eegAusschreibungStatus,
  finanzierungStatus,
  bauStatus,
  flaechenakquiseOptions,
  planungsrechtOptions,
  bimschgOptions,
  eegAusschreibungOptions,
  finanzierungOptions,
  bauOptions,
  canEdit,
  onFlaechenakquiseChange,
  onPlanungsrechtChange,
  onBimschgChange,
  onEegAusschreibungChange,
  onFinanzierungChange,
  onBauChange,
}) => {
  const gesamtstatusColor = getGesamtstatusColor(
    gesamtstatusDomain,
    gesamtstatusAbgeschlossen,
  );
  const gesamtstatusLabel = getGesamtstatusLabel(gesamtstatus || "Neu");

  // Batch-Darstellung wie beim Flurstück-Eigentümer-Status: eine Farbe je Domäne
  // (Dropdown.getColor), "nicht gestartet" erscheint dagegen kursiv/gedimmt in Grau.
  const domainDropdownProps = (
    domain: GesamtstatusDomain,
    options: string[],
  ) => ({
    getColor: (v: string) => getDomainStatusColor(domain, v, options),
    isPlaceholder: (v: string) => isPlaceholderStatus(v, options),
  });

  return (
    <Section title="Projektstatus">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
        <Typography
          sx={{
            width: LABEL_WIDTH,
            minWidth: LABEL_WIDTH,
            whiteSpace: "nowrap",
            color: colors.text.primary,
            fontWeight: 400,
          }}
        >
          Gesamtstatus
        </Typography>
        {gesamtstatusLabel.tooltip ? (
          <Tooltip title={gesamtstatusLabel.tooltip}>
            <span>
              <StatChip
                value={gesamtstatusLabel.label}
                backgroundColor={gesamtstatusColor.background}
                valueColor={gesamtstatusColor.main}
                bold
              />
            </span>
          </Tooltip>
        ) : (
          <StatChip
            value={gesamtstatusLabel.label}
            backgroundColor={gesamtstatusColor.background}
            valueColor={gesamtstatusColor.main}
            bold
          />
        )}
      </Box>

      <ExpandableDetails
        details={
          <Grid container spacing={3} sx={{ pt: 3 }}>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DomainLabel
                  domain="flaechenakquise"
                  value={flaechenakquiseStatus}
                  options={flaechenakquiseOptions}
                  label="Fläche"
                />
                <Dropdown
                  value={flaechenakquiseStatus}
                  options={flaechenakquiseOptions}
                  onChange={onFlaechenakquiseChange}
                  disabled={!canEdit}
                  inputWidth={INPUT_MAX_WIDTH}
                  {...domainDropdownProps(
                    "flaechenakquise",
                    flaechenakquiseOptions,
                  )}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DomainLabel
                  domain="planungsrecht"
                  value={planungsrechtStatus}
                  options={planungsrechtOptions}
                  label="Planungsrecht"
                />
                <Dropdown
                  value={planungsrechtStatus}
                  options={planungsrechtOptions}
                  onChange={onPlanungsrechtChange}
                  disabled={!canEdit}
                  inputWidth={INPUT_MAX_WIDTH}
                  {...domainDropdownProps(
                    "planungsrecht",
                    planungsrechtOptions,
                  )}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DomainLabel
                  domain="bimschg"
                  value={bimschgStatus}
                  options={bimschgOptions}
                  label="Genehmigung"
                />
                <Dropdown
                  value={bimschgStatus}
                  options={bimschgOptions}
                  onChange={onBimschgChange}
                  disabled={!canEdit}
                  inputWidth={INPUT_MAX_WIDTH}
                  {...domainDropdownProps("bimschg", bimschgOptions)}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DomainLabel
                  domain="eeg"
                  value={eegAusschreibungStatus}
                  options={eegAusschreibungOptions}
                  label="Ausschreibung"
                />
                <Dropdown
                  value={eegAusschreibungStatus}
                  options={eegAusschreibungOptions}
                  onChange={onEegAusschreibungChange}
                  disabled={!canEdit}
                  inputWidth={INPUT_MAX_WIDTH}
                  {...domainDropdownProps("eeg", eegAusschreibungOptions)}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DomainLabel
                  domain="finanzierung"
                  value={finanzierungStatus}
                  options={finanzierungOptions}
                  label="Finanzierung"
                />
                <Dropdown
                  value={finanzierungStatus}
                  options={finanzierungOptions}
                  onChange={onFinanzierungChange}
                  disabled={!canEdit}
                  inputWidth={INPUT_MAX_WIDTH}
                  {...domainDropdownProps("finanzierung", finanzierungOptions)}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DomainLabel
                  domain="bau"
                  value={bauStatus}
                  options={bauOptions}
                  label="Bau"
                />
                <Dropdown
                  value={bauStatus}
                  options={bauOptions}
                  onChange={onBauChange}
                  disabled={!canEdit}
                  inputWidth={INPUT_MAX_WIDTH}
                  {...domainDropdownProps("bau", bauOptions)}
                />
              </Box>
            </Grid>
          </Grid>
        }
      />
    </Section>
  );
};

export default React.memo(ProjectStatusSection);
